// Utilidades de fecha/hora para BLOKKO.
//
// El back envía timestamps en UTC, pero el formato NO es homogéneo entre
// endpoints:
//   - "2024-06-05T14:30:45.123Z"  (ISO con Z)
//   - "2024-06-05 14:30:45"       (espacio, sin TZ -> asumir UTC)
//   - "2024-06-05T14:30:45+00:00" (offset)
//   - "2024-06-05T14:30:45.123456" (naive con microsegundos -> asumir UTC)
//
// parseBackendDate normaliza cualquiera de esos a un Date correcto.
// Para mostrar en pantalla y exports de reportes usar formatUtcToLocal (huso del navegador).
// formatUtc se mantiene disponible para casos que requieran UTC explícito.
// Para filtros outbound (UI → back): localDayStartToUtcDate / localDayEndToUtcDate.

const DEFAULT_DATETIME_OPTS = {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

/**
 * Normaliza un valor de fecha del back a un objeto Date UTC-correcto.
 * Detecta si el string trae Z u offset ±HH:MM; si no trae ninguno, asume UTC.
 * @returns {Date|null} Date válido, o null si el valor es vacío/inválido.
 */
export const parseBackendDate = (value) => {
  if (value == null || value === "") return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  // "2024-06-05 14:30:45" -> "2024-06-05T14:30:45"
  let s = String(value).trim().replace(" ", "T");
  // Recortar fracciones de segundo a milisegundos (JS Date solo soporta 3 díg.)
  s = s.replace(/(\.\d{3})\d+/, "$1");
  // Si NO termina en Z ni en offset ±HH:MM / ±HHMM, asumir UTC.
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(s)) s += "Z";

  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Formatea un timestamp del back en el huso horario LOCAL del navegador.
 * Uso: pantalla / UI.
 */
export const formatUtcToLocal = (value, opts) => {
  const d = parseBackendDate(value);
  if (!d) return value ? String(value) : "";
  return d.toLocaleString("en-US", { ...DEFAULT_DATETIME_OPTS, ...opts });
};

const pad2 = (n) => String(n).padStart(2, "0");

/**
 * Normaliza una fecha de calendario local (YYYYMMDD o YYYY-MM-DD).
 * @returns {{ year: number, month: number, day: number }|null}
 */
export const parseLocalCalendarDate = (value) => {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  let year;
  let month;
  let day;

  if (/^\d{8}$/.test(s)) {
    year = parseInt(s.slice(0, 4), 10);
    month = parseInt(s.slice(4, 6), 10);
    day = parseInt(s.slice(6, 8), 10);
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    [year, month, day] = s.split("-").map((part) => parseInt(part, 10));
  } else {
    return null;
  }

  const probe = new Date(year, month - 1, day);
  if (
    probe.getFullYear() !== year ||
    probe.getMonth() !== month - 1 ||
    probe.getDate() !== day
  ) {
    return null;
  }

  return { year, month, day };
};

const formatDateParts = (year, month, day, outputFormat) => {
  if (outputFormat === "YYYY-MM-DD") {
    return `${year}-${pad2(month)}-${pad2(day)}`;
  }
  return `${year}${pad2(month)}${pad2(day)}`;
};

const utcDateStringFromDate = (date, outputFormat) =>
  formatDateParts(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
    outputFormat,
  );

const localWallClockToUtcDate = (
  year,
  month,
  day,
  hour,
  minute,
  second,
  ms,
  outputFormat,
) => {
  const offsetMin = new Date(
    year,
    month - 1,
    day,
    hour,
    minute,
    second,
    ms,
  ).getTimezoneOffset();
  const utcMs =
    Date.UTC(year, month - 1, day, hour, minute, second, ms) +
    offsetMin * 60 * 1000;
  return utcDateStringFromDate(new Date(utcMs), outputFormat);
};

/**
 * Convierte el inicio del día local (00:00:00.000) a fecha UTC para el API.
 */
export const localDayStartToUtcDate = (value, outputFormat = "YYYYMMDD") => {
  const parts = parseLocalCalendarDate(value);
  if (!parts) return "";
  const { year, month, day } = parts;
  return localWallClockToUtcDate(year, month, day, 0, 0, 0, 0, outputFormat);
};

/**
 * Convierte el fin del día local (23:59:59.999) a fecha UTC para el API.
 */
export const localDayEndToUtcDate = (value, outputFormat = "YYYYMMDD") => {
  const parts = parseLocalCalendarDate(value);
  if (!parts) return "";
  const { year, month, day } = parts;
  return localWallClockToUtcDate(
    year,
    month,
    day,
    23,
    59,
    59,
    999,
    outputFormat,
  );
};

/**
 * Lista de fechas UTC (inclusive) que cubren un rango de días locales.
 */
export const getUtcDateRangeForLocalRange = (
  startValue,
  endValue,
  outputFormat = "YYYYMMDD",
) => {
  const utcStart = localDayStartToUtcDate(startValue, outputFormat);
  const utcEnd = localDayEndToUtcDate(endValue, outputFormat);
  if (!utcStart || !utcEnd) return [];

  const startParts = parseLocalCalendarDate(
    utcStart.includes("-") ? utcStart.replaceAll("-", "") : utcStart,
  );
  const endParts = parseLocalCalendarDate(
    utcEnd.includes("-") ? utcEnd.replaceAll("-", "") : utcEnd,
  );
  if (!startParts || !endParts) return [];

  const dates = [];
  let current = Date.UTC(startParts.year, startParts.month - 1, startParts.day);
  const endMs = Date.UTC(endParts.year, endParts.month - 1, endParts.day);

  while (current <= endMs) {
    dates.push(utcDateStringFromDate(new Date(current), outputFormat));
    current += 24 * 60 * 60 * 1000;
  }

  return dates;
};

/**
 * Formatea una fecha de calendario LOCAL a YYYYMMDD SIN convertir a UTC.
 * Uso: filtros outbound de reportes que agrupan por día local (el back recibe además
 * el offset vía tzOffsetMinutes y hace el bucketing en el huso del usuario).
 * @returns {string} "YYYYMMDD", o "" si el valor es inválido.
 */
export const localCalendarDateToYyyymmdd = (value) => {
  const parts = parseLocalCalendarDate(value);
  if (!parts) return "";
  const { year, month, day } = parts;
  return formatDateParts(year, month, day, "YYYYMMDD");
};

/**
 * Offset del huso local en minutos según Date.getTimezoneOffset()
 * (minutos a SUMAR a la hora local para obtener UTC, ej. GMT-5 -> 300).
 * Si se pasa una fecha de calendario local, se usa el offset vigente ESE día
 * (correcto ante cambios de horario de verano); si no, el offset actual.
 * @returns {number} offset en minutos.
 */
export const getLocalTzOffsetMinutes = (value) => {
  const parts = value != null ? parseLocalCalendarDate(value) : null;
  const probe = parts
    ? new Date(parts.year, parts.month - 1, parts.day)
    : new Date();
  return probe.getTimezoneOffset();
};

/**
 * Formatea un timestamp del back manteniéndolo en UTC.
 */
export const formatUtc = (value, opts) => {
  const d = parseBackendDate(value);
  if (!d) return value ? String(value) : "";
  return d.toLocaleString("en-US", {
    timeZone: "UTC",
    ...DEFAULT_DATETIME_OPTS,
    ...opts,
  });
};

/**
 * Abreviatura/offset del huso horario local del navegador, ej. "EST", "GMT-5".
 * Útil para etiquetar las horas convertidas en la UI.
 */
export const getLocalTzLabel = () => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
};
